import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useOrgId } from "@/lib/pulse/useEmployee";
import { mySurveyProgress } from "@/lib/pulse/surveyApi";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

/**
 * Live scorecard shown during a Charles conversation so the person can see
 * exactly what score was recorded against each anchor as it happens.
 */
export function LiveScorePanel() {
  const orgId = useOrgId();
  const { data } = useQuery({
    queryKey: ["my_survey_progress", orgId],
    queryFn: async () => {
      const { data, error } = await mySurveyProgress(orgId!);
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const [flash, setFlash] = useState<string | null>(null);
  const prevRef = useRef<Record<string, number | null>>({});

  useEffect(() => {
    if (!data || data.active !== true) return;
    const prev = prevRef.current;
    const next: Record<string, number | null> = {};
    let changed: string | null = null;
    for (const a of data.anchors) {
      next[a.id] = a.answered_value;
      if (a.answered_value !== null && prev[a.id] !== a.answered_value) changed = a.id;
    }
    prevRef.current = next;
    if (changed && Object.keys(prev).length > 0) {
      setFlash(changed);
      const t = setTimeout(() => setFlash(null), 2200);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (!data || data.active !== true) return null;

  const pct = Math.round((data.answered / Math.max(1, data.total_anchors)) * 100);

  return (
    <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white/80 backdrop-blur p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-neutral-900">{data.survey_topic}</p>
        <p className="text-xs text-neutral-500">
          {data.answered} of {data.total_anchors} scored
        </p>
      </div>
      <Progress value={pct} />
      <ul className="space-y-1.5">
        {data.anchors.map((a) => {
          const scored = a.answered_value !== null;
          return (
            <li
              key={a.id}
              className={cn(
                "flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm transition-colors",
                flash === a.id ? "bg-primary/10" : "bg-transparent",
              )}
            >
              <span className={cn("truncate", scored ? "text-neutral-900" : "text-neutral-400")}>
                {a.name}
              </span>
              {scored ? (
                <span className="flex items-center gap-1 shrink-0 text-primary font-semibold tabular-nums">
                  <Check className="h-3.5 w-3.5" />
                  {a.answered_value}/5
                </span>
              ) : (
                <span className="shrink-0 text-xs text-neutral-400">not yet</span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-neutral-500">
        Scores appear here the moment Charles records them. If one looks wrong, say so and he'll
        correct it.
      </p>
    </div>
  );
}
